<script setup lang="ts">
import {
    defaultCheckpointHubProvider,
    defaultEventHubProvider,
    EHubProvider,
    type CheckpointHubProvider,
    type EventHubProvider
} from '@/types/enum/HubProviderEnum'
import type { DetectionFilters } from '@/types/filters/DetectionFilters'
import type { EventQueryResponse } from '@/types/filters/EventFilters'
import type { CheckpointModel } from '@/types/models/CheckpointModel'
import type { DetectionModel } from '@/types/models/DetectionModel'
import type { EventModel } from '@/types/models/EventModel'
import { DebouncedInputField, GridLayout } from '@rfc/rfc-components-library'
import { useGridSize } from '@rfc/rfc-components-library/hooks'
import {
    defaultQueryProvider,
    EProviders,
    type QueryProvider
} from '@rfc/rfc-components-library/types'
import { computed, inject, nextTick, type Ref } from 'vue'
import type { AssociatedDeviceModel } from '../../../types/models/DeviceModel'
import EventCheckpointAutocomplete from '../EventCheckpointAutocomplete.vue'
import DetectionDeviceFilters from './components/DetectionDeviceFilters.vue'

const isViewerOnly = inject<boolean>(EHubProvider.IS_VIEWER_ONLY, false)
const { event } = inject<EventHubProvider>(EHubProvider.EVENT, defaultEventHubProvider)
const { checkpoint, handleChangeCheckpoint } = inject<CheckpointHubProvider>(
    EHubProvider.CHECKPOINT,
    defaultCheckpointHubProvider
)
const { query } = inject<{ query?: Ref<EventQueryResponse> }>(EProviders.DETECTION, {
    query: undefined
})
const { deviceQuery } = inject<QueryProvider>(EProviders.QUERY, defaultQueryProvider)

type Props = {
    filters: DetectionFilters
    selectedEvent?: Partial<EventModel>
}

const props = defineProps<Props>()

type Emits = {
    (e: 'change', value: Partial<DetectionModel>): void
}
const emit = defineEmits<Emits>()

const gridSize = useGridSize()

const handleSelectEvent = async (value?: EventModel) =>
    emit('change', { event_id: value?.id, checkpoint_id: undefined, device_id: undefined })
const handleSelectCheckpoint = async (value?: CheckpointModel) => {
    emit('change', { checkpoint_id: value?.id, device_id: undefined })
    await nextTick(() => {
        handleChangeCheckpoint?.(value)
        deviceQuery?.handleChangeDevice(undefined)
    })
}
const handleSelectDevice = (value?: Partial<DetectionModel>) => {
    const changeValue = deviceOptions.value
        ? { ...value }
        : { event_id: undefined, checkpoint_id: undefined, ...value }
    emit('change', changeValue)
}

const deviceFilters = computed(() => ({
    event_id: props.filters?.event_id,
    checkpoint_id: props.filters?.checkpoint_id
}))

const devicesAssociates = computed(() =>
    query?.value?.checkpoints?.reduce(
        (prev: AssociatedDeviceModel[], current) => [
            ...(prev ?? []),
            ...(current.associates ?? [])
        ],
        []
    )
)

const deviceOptions = computed(() =>
    event?.value
        ? (query?.value?.devices ??
          (devicesAssociates.value?.length
              ? devicesAssociates.value.map(item => ({
                    ...item,
                    device_id: item.id ?? item.device_id
                }))
              : []))
        : undefined
)
</script>

<template>
    <GridLayout
        :maxItemPerRow="gridSize"
        :gap="8"
        :rowGap="12"
        class="w-full flex-wrap"
        isResponsive>
        <EventCheckpointAutocomplete
            :event="selectedEvent ?? event"
            :checkpoint="checkpoint"
            keepEventInStorage
            filterAllEvents
            useQueryResponse
            @selectEvent="handleSelectEvent"
            @selectCheckpoint="handleSelectCheckpoint" />
        <DetectionDeviceFilters
            v-if="!isViewerOnly"
            :filters="filters"
            :deviceFilters="deviceFilters"
            :deviceOptions="deviceOptions"
            @change="handleSelectDevice" />
        <slot v-bind="filters" />
        <DebouncedInputField
            :label="$t('detection.filters.bib')"
            :modelValue="filters.bib"
            @change="$emit('change', { bib: $event })" />
    </GridLayout>
</template>
